// ErrorRowsModal.tsx
import React, { useEffect, useState } from "react";
import { X, Download, AlertCircle } from "lucide-react";
import * as XLSX from "xlsx";

interface ErrorRowsModalProps {
  isOpen: boolean;
  onClose: () => void;
  errors: string[]; // Array of error messages like ["Row 2: Invalid Template_ID 13333.", "Row 5: Missing field"]
  originalFile: File | null; // The original file that was uploaded
}

interface ErrorRowData {
  rowNumber: number;
  errorMessage: string;
  data: Record<string, any>;
}

export default function ErrorRowsModal({
  isOpen,
  onClose,
  errors,
  originalFile,
}: ErrorRowsModalProps) {
  const [errorRows, setErrorRows] = useState<ErrorRowData[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  console.log("THIS IS INSIDE THE DRX MODEL", errors);

  useEffect(() => {
    if (isOpen && originalFile && errors.length > 0) {
      parseFileAndExtractErrors();
    }
  }, [isOpen, originalFile, errors]);

  const parseErrorMessages = (errorMessages: string[]): number[] => {
    const rowNumbers: number[] = [];

    errorMessages.forEach((error) => {
      const match = error.match(/Row\s+(\d+)/i);
      if (match) {
        rowNumbers.push(parseInt(match[1]));
      }
    });

    return [...new Set(rowNumbers)]; // Remove duplicates
  };

  const parseCSV = (
    text: string
  ): { headers: string[]; rows: Record<string, any>[] } => {
    const lines = text.split("\n").filter((line) => line.trim());
    if (lines.length === 0) return { headers: [], rows: [] };

    // Parse header
    const headerLine = lines[0];
    const headers = headerLine
      .split(",")
      .map((h) => h.trim().replace(/^"|"$/g, ""));

    // Parse data rows
    const rows: Record<string, any>[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      const row: Record<string, any> = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || "";
      });
      rows.push(row);
    }

    return { headers, rows };
  };

  const parseCSVLine = (line: string): string[] => {
    const values: string[] = [];
    let currentValue = "";
    let insideQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (insideQuotes && nextChar === '"') {
          currentValue += '"';
          i++; // Skip next quote
        } else {
          insideQuotes = !insideQuotes;
        }
      } else if (char === "," && !insideQuotes) {
        values.push(currentValue.trim());
        currentValue = "";
      } else {
        currentValue += char;
      }
    }
    values.push(currentValue.trim());

    return values;
  };

  const parseFileAndExtractErrors = async () => {
    setIsLoading(true);
    const extension = originalFile?.name.split(".").pop()?.toLowerCase();
    const errorRowNumbers = parseErrorMessages(errors);

    try {
      if (extension === "csv") {
        const reader = new FileReader();
        reader.onload = (e) => {
          const text = e.target?.result as string;
          const { headers: fileHeaders, rows: allData } = parseCSV(text);

          const extractedErrors: ErrorRowData[] = errorRowNumbers
            .map((rowNum) => {
              const dataIndex = rowNum - 2; // Row 2 = index 0
              const errorMsg =
                errors.find((e) => e.includes(`Row ${rowNum}`)) || "";

              return {
                rowNumber: rowNum,
                errorMessage: errorMsg,
                data: allData[dataIndex] || {},
              };
            })
            .filter((item) => Object.keys(item.data).length > 0);

          setHeaders(fileHeaders);
          setErrorRows(extractedErrors);
          setIsLoading(false);
        };
        {
          originalFile && reader.readAsText(originalFile);
        }
      } else if (extension === "xlsx" || extension === "xls") {
        const reader = new FileReader();
        reader.onload = (e) => {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: "binary" });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData: any[][] = XLSX.utils.sheet_to_json(firstSheet, {
            header: 1,
          });

          const fileHeaders = jsonData[0] as string[];
          const allData = jsonData.slice(1);

          const extractedErrors: ErrorRowData[] = errorRowNumbers
            .map((rowNum) => {
              const dataIndex = rowNum - 2;
              const row = allData[dataIndex];
              const errorMsg =
                errors.find((e) => e.includes(`Row ${rowNum}`)) || "";

              const rowData: Record<string, any> = {};
              fileHeaders.forEach((header, index) => {
                rowData[header] = row?.[index] !== undefined ? row[index] : "";
              });

              return {
                rowNumber: rowNum,
                errorMessage: errorMsg,
                data: rowData,
              };
            })
            .filter((item) => Object.keys(item.data).length > 0);

          setHeaders(fileHeaders);
          setErrorRows(extractedErrors);
          setIsLoading(false);
        };
        {
          originalFile && reader.readAsBinaryString(originalFile);
        }
      }
    } catch (error) {
      console.error("Error parsing file:", error);
      setIsLoading(false);
    }
  };

  const downloadErrorCSV = () => {
    if (errorRows.length === 0) return;

    // Convert to CSV manually
    const csvRows: string[] = [];

    // Add headers
    csvRows.push(headers.map((h) => `"${h}"`).join(","));

    // Add data rows
    errorRows.forEach((err) => {
      const values = headers.map((header) => {
        const value = err.data[header];
        const stringValue =
          value !== undefined && value !== null ? String(value) : "";
        // Escape quotes and wrap in quotes if contains comma or quote
        if (
          stringValue.includes(",") ||
          stringValue.includes('"') ||
          stringValue.includes("\n")
        ) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      });
      csvRows.push(values.join(","));
    });

    const csv = csvRows.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    const baseName = originalFile?.name.replace(/\.[^/.]+$/, "");
    link.setAttribute("href", url);
    link.setAttribute("download", `${baseName}_errors.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                Upload Errors Detected
              </h2>
              <p className="text-sm text-gray-600">
                {errorRows.length} row(s) have validation errors
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Error Summary */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-800 mb-2">
                  Error Summary:
                </h3>
                <ul className="space-y-1">
                  {errorRows.map((err, idx) => (
                    <li key={idx} className="text-sm text-red-700">
                      <span className="font-medium">Row {err.rowNumber}:</span>{" "}
                      {err.errorMessage.replace(/^Row \d+:\s*/, "")}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Data Preview */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">
                  Error Rows Data:
                </h3>
                <div className="border rounded-lg overflow-auto max-h-96">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">
                          Row #
                        </th>
                        {headers.map((header, idx) => (
                          <th
                            key={idx}
                            className="px-4 py-3 text-left font-semibold text-gray-700 border-b"
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {errorRows.map((err, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-red-600">
                            {err.rowNumber}
                          </td>
                          {headers.map((header, headerIdx) => (
                            <td
                              key={headerIdx}
                              className="px-4 py-3 text-gray-700"
                            >
                              {err.data[header] !== undefined &&
                              err.data[header] !== null
                                ? String(err.data[header])
                                : "-"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 rounded-b-2xl">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Download the error rows to fix and re-upload
            </p>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Close
              </button>
              <button
                onClick={downloadErrorCSV}
                disabled={errorRows.length === 0}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download size={20} />
                Download Error Rows
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
